<template>
  <div class="pdf-preview__modal">
    <div class="pdf-container"
         @contextmenu.prevent
         @selectstart.prevent>
      <!-- PDF viewer using PDF.js -->
      <div v-if="loading"
           class="pdf-loading">
        <div class="loading-spinner"></div>
        <p>{{ $gettext('Loading PDF...') }}</p>
      </div>
      <div v-else-if="error"
           class="pdf-error">
        <p>{{ $gettext('Error loading PDF') }}</p>
        <button class="sk-btn sk-btn--default"
                @click="loadPdf">{{ $gettext('Retry') }}</button>
      </div>
      <div v-else-if="pdfBlobUrl"
           class="pdf-viewer">
        <!-- Navigation controls - positioned above the PDF content -->
        <div class="pdf-navigation">
          <button class="nav-btn"
                  :disabled="currentPage <= 1"
                  :title="$gettext('Previous page (← or Page Up)')"
                  @click="goToPreviousPage">‹</button>
          <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
          <button class="nav-btn"
                  :disabled="currentPage >= totalPages"
                  :title="$gettext('Next page (→ or Page Down)')"
                  @click="goToNextPage">›</button>
          <button class="nav-btn"
                  :title="$gettext('Zoom in (Ctrl/Cmd + +)')"
                  @click="zoomIn">+</button>
          <button class="nav-btn"
                  :title="$gettext('Zoom out (Ctrl/Cmd + -)')"
                  @click="zoomOut">−</button>
        </div>

        <div ref="pdfContainer"
             class="pdf-container-inner"
             @contextmenu.prevent
             @selectstart.prevent
             @mousedown="handleMouseDown"
             @mouseup="handleMouseUp">
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {mapGetters, mapState} from 'vuex';

  export default {
    props: {
      data: {
        type: Object,
        default: () => {}
      }
    },
    data() {
      return {
        loading: true,
        error: false,
        pdfBlobUrl: null,
        pdfDoc: null,
        currentPage: 1,
        totalPages: 0,
        scale: 1.0,
        devToolsInterval: null,
        keyboardHandler: null
      };
    },
    computed: {
      ...mapState('OneProjectStore', {
        project: ({project}) => project
      }),
      ...mapGetters('UserInfoStore', [
        'userIsInterpreter'
      ]),
      orderId() {
        return this.project?.id;
      },
      attachmentId() {
        // Extract attachment ID from the file link or use file.id if available
        if (this.data.file.id) {
          return this.data.file.id;
        }
        // Fallback: try to extract from link if it contains the ID
        const linkParts = this.data.file.link?.split('/');
        return linkParts?.[linkParts.length - 1];
      },
    },
    methods: {
      async loadPdf() {
        if (!this.orderId || !this.attachmentId) {
          this.error = true;
          this.loading = false;
          return;
        }

        try {
          this.loading = true;
          this.error = false;

          const blob = await this.$fetcher.downloadAttachment(this.orderId, this.attachmentId);

          if (blob) {
            // Create blob URL for the PDF
            this.pdfBlobUrl = URL.createObjectURL(blob);

            // Set loading to false first so the template renders the pdfContainer
            this.loading = false;

            // Wait for the next tick to ensure the DOM is updated
            await this.$nextTick();

            // Now render the PDF
            await this.renderPdf(blob);
          } else {
            throw new Error('No blob data received');
          }
        } catch (error) {
          this.error = true;
          this.loading = false;
        }
      },
      async renderPdf(blob) {
        // Check if container exists
        if (!this.$refs.pdfContainer) {
          // Wait a bit more for the DOM to update
          await this.$nextTick();
          if (!this.$refs.pdfContainer) {
            return;
          }
        }

        // Dynamically import PDF.js
        const pdfjsLib = await import('pdfjs-dist');

        // Set worker path
        pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.16.105/pdf.worker.min.js';

        // Load the PDF document
        const arrayBuffer = await blob.arrayBuffer();
        this.pdfDoc = await pdfjsLib.getDocument({data: arrayBuffer}).promise;
        this.totalPages = this.pdfDoc.numPages;

        // Render the first page
        await this.renderPage(1);
      },
      async renderPage(pageNumber) {
        try {
          // Check if container exists
          if (!this.$refs.pdfContainer) {
            return;
          }

          // Validate page number
          if (!pageNumber || pageNumber < 1 || pageNumber > this.totalPages) {
            this.error = true;
            return;
          }

          // Check if PDF document is available
          if (!this.pdfDoc) {
            this.error = true;
            return;
          }

          const page = await this.pdfDoc.getPage(pageNumber);

          const viewport = page.getViewport({scale: this.scale});
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');

          // Validate canvas context
          if (!context) {
            this.error = true;
            return;
          }

          canvas.height = viewport.height;
          canvas.width = viewport.width;

          const renderContext = {
            canvasContext: context,
            viewport: viewport
          };

          // Clear container
          const container = this.$refs.pdfContainer;
          container.innerHTML = '';
          container.appendChild(canvas);

          // Render the page
          await page.render(renderContext).promise;

          this.currentPage = pageNumber;
          this.error = false; // Clear any previous errors on successful render
        } catch (error) {
          // Set error state to show error UI
          this.error = true;
          this.errorMessage = this.$gettext('Failed to render page');
        }
      },
      goToPreviousPage() {
        if (this.currentPage > 1) {
          this.renderPage(this.currentPage - 1);
        }
      },
      goToNextPage() {
        if (this.currentPage < this.totalPages) {
          this.renderPage(this.currentPage + 1);
        }
      },
      zoomIn() {
        this.scale = Math.min(this.scale * 1.2, 3.0);
        this.renderPage(this.currentPage);
      },
      zoomOut() {
        this.scale = Math.max(this.scale / 1.2, 0.5);
        this.renderPage(this.currentPage);
      },
      handleMouseDown(event) {
        // Prevent right mouse button
        if (event.button === 2) {
          event.preventDefault();
          return false;
        }
      },
      handleMouseUp(event) {
        // Prevent right mouse button
        if (event.button === 2) {
          event.preventDefault();
          return false;
        }
      },
      disableKeyboardShortcuts() {
        // Disable common keyboard shortcuts for save/download
        this.keyboardHandler = (e) => {
          // Ctrl+S, Ctrl+Shift+S, F12, Ctrl+Shift+I, Ctrl+U
          if ((e.ctrlKey && (e.key === 's' || e.key === 'S' || e.key === 'u' || e.key === 'U'))
            || (e.ctrlKey && e.shiftKey && (e.key === 'i' || e.key === 'I'))
            || e.key === 'F12') {
            e.preventDefault();
            e.stopPropagation();
            return false;
          }

          // Add PDF navigation keyboard shortcuts
          if (this.pdfDoc && !this.loading && !this.error) {
            switch (e.key) {
              case 'ArrowLeft':
              case 'PageUp':
                e.preventDefault();
                this.goToPreviousPage();
                break;
              case 'ArrowRight':
              case 'PageDown':
                e.preventDefault();
                this.goToNextPage();
                break;
              case '+':
              case '=':
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  this.zoomIn();
                }
                break;
              case '-':
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  this.zoomOut();
                }
                break;
            }
          }
        };
        document.addEventListener('keydown', this.keyboardHandler, true);
      },
      disableDevTools() {
        // Basic dev tools detection
        const devtools = {open: false, orientation: null};
        this.devToolsInterval = setInterval(() => {
          const threshold = 160;
          if (window.outerHeight - window.innerHeight > threshold
            || window.outerWidth - window.innerWidth > threshold) {
            if (!devtools.open) {
              devtools.open = true;
              // Optionally show a warning or take action
            }
          } else {
            devtools.open = false;
          }
        }, 500);
      },
      overrideModalWidth() {
        // Find the modal wrapper and override its width
        const modalWrapper = document.querySelector('.sk-modal__wrapper');
        if (modalWrapper) {
          modalWrapper.style.setProperty('width', 'auto', 'important');
          modalWrapper.style.setProperty('max-width', '90vw', 'important');
        }
      }
    },
    mounted() {
      // Wait for the next tick to ensure the DOM is fully rendered
      this.$nextTick(() => {
        // Load the PDF when component is mounted
        this.loadPdf();

        // Override the modal wrapper width to remove the 600px constraint
        this.overrideModalWidth();
      });

      // Additional security measures
      this.disableKeyboardShortcuts();
      this.disableDevTools();
    },
    beforeDestroy() {
      // Clean up blob URL to prevent memory leaks
      if (this.pdfBlobUrl) {
        URL.revokeObjectURL(this.pdfBlobUrl);
      }

      // Clean up interval to prevent memory leaks
      if (this.devToolsInterval) {
        clearInterval(this.devToolsInterval);
      }

      // Clean up event listener to prevent memory leaks
      if (this.keyboardHandler) {
        document.removeEventListener('keydown', this.keyboardHandler, true);
      }
    }
  };
</script>

<style>

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Override the hardcoded 600px width from sk-modal wrapper */
.pdf-preview__modal {
  position: relative;
  display: flex;
  flex-direction: column;
  width: auto !important;
  width: 100%;
  max-width: 90vw !important;
  height: auto;
  margin-top: -15px;
  padding: 45px 30px 20px;
}

.pdf-container {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 600px;
  border: 1px solid #ddd;
  border-radius: 4px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.pdf-navigation {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  flex-shrink: 0;
  gap: 10px;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px;
  border-bottom: 1px solid #ddd;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
}

.nav-btn {
  min-width: 40px;
  margin: 0 !important;
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  font-weight: bold;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.nav-btn:hover:not(:disabled) {
  border-color: #999;
  background: #f5f5f5;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.nav-btn:active:not(:disabled) {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  transform: translateY(0);
}

.nav-btn:disabled {
  box-shadow: none;
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.page-info {
  min-width: 60px;
  margin: 0 15px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
  text-align: center;
}

.pdf-container p {
  padding: 20px;
  color: #666;
  text-align: center;
}

.pdf-loading,
.pdf-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 20px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  margin-bottom: 20px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.pdf-error p {
  margin-bottom: 20px;
  color: #e74c3c;
}

.pdf-viewer {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
}

.pdf-container-inner {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  overflow: auto;
  width: 100%;
}

@media (max-width: 768px) {
  .pdf-navigation {
    gap: 8px;
    padding: 8px;
  }

  .nav-btn {
    min-width: 36px;
    padding: 6px 10px;
    font-size: 14px;
  }

  .page-info {
    min-width: 50px;
    margin: 0 10px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .pdf-navigation {
    gap: 6px;
    padding: 6px;
  }

  .nav-btn {
    min-width: 32px;
    padding: 5px 8px;
    font-size: 13px;
  }

  .page-info {
    min-width: 45px;
    margin: 0 8px;
    font-size: 12px;
  }
}
</style>
